import { useState, useEffect, useCallback, useContext, useRef } from "react";
import { useAuthState } from "react-firebase-hooks/auth";
import { Navigate } from "react-router-dom";
import { auth, db } from "../firebase";
import { collection, getDocs, query, orderBy, doc, updateDoc } from "firebase/firestore";
import MobileNav from "./MobileNav";
import Cycles from "./Cycles";
import JournalTab from "./JournalTab";
import PromptsTab from "./PromptsTab";
import ScheduleTab from "./ScheduleTab";
import PreviousEntriesTab from "./PreviousEntriesTab";
import SettingsTab from "./SettingsTab";
import JournalBottomNav from "./JournalBottomNav";
import { formatDate } from "../utils/formatDate";
import { decryptText, getEncryptionSecret } from "../utils";
import { EncryptionContext } from "./EncryptionContext";
import { throttle } from "lodash";

function parseDaysOfWeek(dayString) {
  if (!dayString) return [];
  if (Array.isArray(dayString)) return dayString;
  return dayString
    .split(",")
    .map((day) => day.trim())
    .filter(Boolean);
}

const getTodayDate = () => new Date().toISOString().split("T")[0];

function JournalApp({ initialTab = "journal" }) {
  const [user] = useAuthState(auth);
  const { passphrase } = useContext(EncryptionContext);
  const [tab, setTab] = useState(initialTab);
  const [selectedCycle, setSelectedCycle] = useState(null);
  const [prompts, setPrompts] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split("T")[0]);
  const [decryptionError, setDecryptionError] = useState("");
  const [loading, setLoading] = useState({ prompts: false, tasks: false });
  const cache = useRef(new Map());
  const fetchRef = useRef(null);

  console.log("JournalApp render, user:", user?.uid, "hasOptionalPassphrase:", Boolean(passphrase), "tab:", tab, "selectedCycle:", selectedCycle?.id);

  const restrictedTabs = ["journal", "prompts", "schedule", "previous", "settings", "cycles"];

  // Redirect to login if user is not authenticated and trying to access a restricted tab
  if (restrictedTabs.includes(tab) && !user) {
    return <Navigate to="/login" replace />;
  }

  const fetchData = useCallback(async (cycle) => {
    console.log("fetchData called with user:", user?.uid, "cycle:", cycle?.id);
    if (!user || !cycle) {
      console.log("fetchData early exit: user:", !!user, "cycle:", !!cycle);
      setPrompts([]);
      setTasks([]);
      return;
    }
    const encryptionSecret = getEncryptionSecret(user, passphrase);

    const cyclePath = `users/${user.uid}/cycles/${cycle.id}`;
    const cacheKey = `tasks-${cycle.id}`;
    const promptCacheKey = `prompts-${cycle.id}`;

    try {
      // Clear cache to ensure fresh data
      cache.current.delete(promptCacheKey);
      cache.current.delete(cacheKey);

      // Fetch prompts
      setLoading((prev) => ({ ...prev, prompts: true }));
      const promptSnapshot = await getDocs(collection(db, `${cyclePath}/prompts`));
      console.log("Prompts fetched, count:", promptSnapshot.docs.length);
      const newPrompts = await Promise.all(
        promptSnapshot.docs.map(async (doc) => {
          const data = doc.data();
          console.log("Processing prompt:", doc.id, "data:", data);
          return {
            id: doc.id,
            ...data,
            text: await decryptText(data.text, user, encryptionSecret, setDecryptionError),
          };
        })
      );
      console.log("Processed prompts:", newPrompts);
      setPrompts(newPrompts);
      cache.current.set(promptCacheKey, newPrompts);

      // Fetch all tasks
      setLoading((prev) => ({ ...prev, tasks: true }));
      const taskQuery = query(
        collection(db, `${cyclePath}/schedules/shared/tasks`),
        orderBy("createdAt", "desc")
      );
      const taskSnapshot = await getDocs(taskQuery);
      console.log("Tasks fetched, count:", taskSnapshot.docs.length);
      const newTasks = await Promise.all(
        taskSnapshot.docs.map(async (doc) => {
          const data = doc.data();
          console.log("Processing task:", doc.id, "data:", data);
          const decryptedDayOfWeek = await decryptText(data.dayOfWeek || "", user, encryptionSecret, setDecryptionError);
          console.log("Decrypted dayOfWeek for task", doc.id, ":", decryptedDayOfWeek);
          return {
            id: doc.id,
            ...data,
            text: await decryptText(data.text, user, encryptionSecret, setDecryptionError),
            time: await decryptText(data.time || "", user, encryptionSecret, setDecryptionError),
            priority: await decryptText(data.priority || "Medium", user, encryptionSecret, setDecryptionError),
            dayOfWeek: parseDaysOfWeek(decryptedDayOfWeek),
            completed: data.completed ?? false,
            lastCompletedDate: data.lastCompletedDate || null,
          };
        })
      );
      console.log("Processed tasks:", newTasks);

      const todayDate = getTodayDate();
      const tasksToReset = [];
      const normalizedTasks = newTasks.map((task) => {
        if (task.completed && task.lastCompletedDate !== todayDate) {
          tasksToReset.push(task.id);
          return { ...task, completed: false, lastCompletedDate: null };
        }
        return task;
      });

      setTasks(normalizedTasks);
      cache.current.set(cacheKey, normalizedTasks);
      setDecryptionError("");

      if (tasksToReset.length > 0) {
        console.log("Resetting completed status for tasks:", tasksToReset);
        await Promise.all(
          tasksToReset.map((taskId) =>
            updateDoc(doc(db, `${cyclePath}/schedules/shared/tasks`, taskId), {
              completed: false,
              lastCompletedDate: null,
            })
          )
        );
      }
    } catch (error) {
      console.error("Fetch data error:", error, "code:", error.code);
      setDecryptionError("Error fetching data. Please check your network and try again.");
    } finally {
      setLoading((prev) => ({ ...prev, prompts: false, tasks: false }));
    }
  }, [user, passphrase, setDecryptionError]);

  useEffect(() => {
    const throttledFetch = throttle((cycle) => {
      fetchData(cycle);
    }, 500);

    fetchRef.current = throttledFetch;

    return () => {
      throttledFetch.cancel();
    };
  }, [fetchData]);

  useEffect(() => {
    if (selectedCycle && fetchRef.current) {
      console.log("Selected cycle changed, fetching data for:", selectedCycle.id);
      cache.current.clear();
      fetchRef.current(selectedCycle);
    }
  }, [selectedCycle, fetchData]);

  useEffect(() => {
    const handleTaskUpdate = (event) => {
      const { taskId, completed, lastCompletedDate } = event.detail;
      setTasks((prevTasks) =>
        prevTasks.map((task) =>
          task.id === taskId ? { ...task, completed, lastCompletedDate } : task
        )
      );
    };
    window.addEventListener("updateTasks", handleTaskUpdate);
    return () => window.removeEventListener("updateTasks", handleTaskUpdate);
  }, []);

  return (
    <div className="bg-background-light dark:bg-background-dark flex min-h-screen flex-col overflow-x-hidden">
      <div className="flex flex-1">
        <MobileNav activeTab={tab} setTab={setTab} />
        <div className="w-full flex-grow px-4 pt-20 sm:pt-16">
          {decryptionError && (
            <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 bg-red-100 dark:bg-red-900 text-error p-3 rounded shadow-md max-w-md">
              {decryptionError}
            </div>
          )}
          {(loading.prompts || loading.tasks) && (
            <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-50">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
            </div>
          )}
          <div className={(!selectedCycle && (loading.prompts || loading.tasks) && tab !== "cycles" && tab !== "settings") ? "opacity-50 pointer-events-none" : ""}>
            {tab === "cycles" && user ? (
              <Cycles
                user={user}
                db={db}
                setSelectedCycle={setSelectedCycle}
                selectedCycle={selectedCycle}
                setTab={setTab}
              />
            ) : tab === "settings" && user ? (
              <SettingsTab
                user={user}
                setDecryptionError={setDecryptionError}
              />
            ) : !selectedCycle ? (
              <div className="card animate-fade-in text-center w-full mt-0">
                <h2 className="text-2xl font-semibold text-text-light dark:text-text-dark mb-4">No Cycle Selected</h2>
                <p className="text-neutral mb-4">Please select a cycle from the Cycles tab to continue.</p>
                <button onClick={() => setTab("cycles")} className="btn-primary">
                  Go to Cycles
                </button>
              </div>
            ) : (
              <>
                <div className="card mb-6 animate-fade-in w-full mt-0">
                  <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-2 mb-4">
                    <h2 className="text-2xl font-semibold text-text-light dark:text-text-dark">
                      {selectedCycle.name} ({formatDate(selectedCycle.startDate)} to {formatDate(selectedCycle.endDate)})
                    </h2>
                    <button
                      onClick={() => setTab("cycles")}
                      className="btn-secondary flex items-center justify-center gap-2 w-full md:w-auto"
                    >
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M15.707 15.707a1 1 0 01-1.414 0L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 011.414-1.414L10 8.586l4.293-4.293a1 1 0 011.414 1.414L11.414 10l4.293 4.293a1 1 0 010 1.414z" />
                      </svg>
                      Back to Cycles
                    </button>
                  </div>
                  <div className="hidden md:flex gap-3 flex-wrap">
                    <button
                      onClick={() => setTab("journal")}
                      className={`tab ${tab === "journal" ? "tab-active" : "tab-inactive"}`}
                    >
                      Journal
                    </button>
                    <button
                      onClick={() => setTab("prompts")}
                      className={`tab ${tab === "prompts" ? "tab-active" : "tab-inactive"}`}
                    >
                      Prompts
                    </button>
                    <button
                      onClick={() => setTab("schedule")}
                      className={`tab ${tab === "schedule" ? "tab-active" : "tab-inactive"}`}
                    >
                      Schedule
                    </button>
                    <button
                      onClick={() => setTab("previous")}
                      className={`tab ${tab === "previous" ? "tab-active" : "tab-inactive"}`}
                    >
                      Previous Entries
                    </button>
                  </div>
                </div>
                {tab === "journal" && (
                  <JournalTab
                    user={user}
                    db={db}
                    selectedCycle={selectedCycle}
                    setDecryptionError={setDecryptionError}
                    prompts={prompts}
                    tasks={tasks}
                    setTasks={setTasks}
                    selectedDate={selectedDate}
                    loadingTasks={loading.tasks}
                  />
                )}
                {tab === "prompts" && (
                  <PromptsTab
                    user={user}
                    db={db}
                    selectedCycle={selectedCycle}
                    prompts={prompts}
                    setPrompts={setPrompts}
                    setDecryptionError={setDecryptionError}
                  />
                )}
                {tab === "schedule" && (
                  <ScheduleTab
                    user={user}
                    db={db}
                    selectedCycle={selectedCycle}
                    tasks={tasks}
                    setTasks={setTasks}
                    setDecryptionError={setDecryptionError}
                  />
                )}
                {tab === "previous" && (
                  <PreviousEntriesTab
                    user={user}
                    db={db}
                    selectedCycle={selectedCycle}
                    setDecryptionError={setDecryptionError}
                    prompts={prompts}
                    selectedDate={selectedDate}
                  />
                )}
              </>
            )}
          </div>
        </div>
      </div>
      {selectedCycle && tab !== "cycles" && tab !== "settings" && (
        <JournalBottomNav active={tab} onSelect={setTab} />
      )}
    </div>
  );
}

export default JournalApp;